/**
 * ReceiptCard - 收据卡片组件
 * 
 * 显示收据的完整信息，包括类别、标题、金额、上传者、状态和操作按钮
 */
import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, Image, Platform } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { MODULE_COLORS } from '@cubemeet/core';

export interface ReceiptData {
  /**
   * 收据ID
   */
  id: string;
  /** 收据标题 */
  title: string;
  /** 金额（最小货币单位或浮点，视调用方约定） */
  amount: number;
  /** 货币代码 */
  currency: string;
  /** 本地货币金额（可选） */
  localAmount?: number;
  /** 本地货币代码（可选） */
  localCurrency?: string;
  /** 汇率（可选） */
  exchangeRate?: number;
  /** 类别 */
  category?: string;
  /** 上传者信息 */
  uploader?: {
    id?: string | null;
    name?: string | null;
    avatar?: string | null;
    avatar_url?: string | null; // tolerate either field name
    display_name?: string | null;
  } | null;
  /** 上传日期 */
  uploadDate?: string | null;
  /** 状态 */
  status?: 'pending' | 'approved' | 'rejected' | string;
  /** 原始 receipt url */
  receiptUrl?: string | null;
  /**
   * 当为分账/私账时，可能包含成员分账详情
   */
  members?: Array<{
    user_id: string;
    amount: number;
    share?: number | null;
  }>;
}

export interface ReceiptCardProps {
  receipt: ReceiptData;
  onPress?: () => void;
  formatCurrency: (amount: number, currency?: string) => string;
  t?: (key: string) => string;
  onUpload?: () => void;
  onExportPDF?: () => void;
  isExporting?: boolean;
  showActions?: boolean;
  // approval controls
  canApprove?: boolean;
  onApprove?: (receiptId: string) => void;
  onRequestReject?: (receiptId: string) => void;
}

// 类别图标映射 (accept optional category)
const getCategoryIcon = (category?: string): string => {
  const iconMap: Record<string, string> = {
    food: 'restaurant',
    transport: 'directions_car',
    accommodation: 'hotel',
    activities: 'local_activity',
    shopping: 'shopping_bag',
    general: 'receipt',
  };
    if (!category) return 'receipt';
    return iconMap[category] || 'receipt';
};

  // 状态样式映射 (accept optional status)
  const getStatusStyle = (status?: string) => {
  switch (status) {
    case 'approved':
      return {
        backgroundColor: '#e8f5e8',
        color: '#2e7d32',
        text: 'Approved',
      };
    case 'rejected':
      return {
        backgroundColor: '#ffebee',
        color: '#d32f2f',
        text: 'Rejected',
      };
    case 'pending':
    default:
      return {
        backgroundColor: '#fff3e0',
        color: '#f57c00',
        text: 'Pending',
      };
  }
  // default
  return {
    backgroundColor: '#fff3e0',
    color: '#f57c00',
    text: 'Pending',
  };
};

/**
 * ReceiptCard 组件
 */
const ReceiptCard: React.FC<ReceiptCardProps> = ({
  receipt,
  onPress,
  formatCurrency,
  t = (key: string) => key,
  onUpload,
  onExportPDF,
  isExporting = false,
  showActions = true,
  canApprove = false,
  onApprove,
  onRequestReject,
}) => {
  const categoryIcon = getCategoryIcon(receipt.category);
  const statusStyle = getStatusStyle(receipt.status);

  // 格式化日期
  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch (e) {
      return String(dateString);
    }
  };

  const resolveActionLabel = (key: string, fallback: string) => {
    if (!t) return fallback;
    try {
      const v = t(`travelDetail.actions.${key}`);
      if (v && v !== `travelDetail.actions.${key}`) return v;
    } catch (e) {}
    try {
      const v = t(`expenseManagement.${key}`);
      if (v && v !== `expenseManagement.${key}`) return v;
    } catch (e) {}
    return fallback;
  };

  const RenderIcon: React.FC<{ name: string; size?: number; color?: string }> = ({ name, size = 16, color = '#666' }) => {
    // Normalize name to use underscores which matches MaterialIcons ligature names
    const resolvedName = name ? name.replace(/-/g, '_') : name;
    // Use the Icon component on all platforms; react-native-vector-icons is shimmed on web
    return <Icon name={resolvedName} size={size} color={color} />;
  };

  return (
    // Single card wrapper (avoid double-card nesting)
    <TouchableOpacity style={[styles.mainCard, styles.receiptCard]} onPress={onPress}>
        {/* 顶部行：类别图标 + 标题 + 金额 */}
        <View style={styles.topRow}>
          <View style={styles.leftInfo}>
            <View style={styles.categoryIcon}>
              <Icon name={categoryIcon} size={20} color={MODULE_COLORS.travel} />
            </View>
            <Text style={styles.title}>{receipt.title}</Text>
          </View>

          <View style={styles.amountSection}>
            <Text style={styles.amount}>
              {formatCurrency(receipt.amount, receipt.currency)}
              {receipt.localAmount && receipt.localCurrency && receipt.currency !== receipt.localCurrency && (
                <Text style={styles.localAmount}>
                  {' '}({formatCurrency(receipt.localAmount, receipt.localCurrency)})
                </Text>
              )}
            </Text>
            {receipt.exchangeRate && (
              <Text style={styles.exchangeRate}>
                汇率: {receipt.exchangeRate.toFixed(4)}
              </Text>
            )}
          </View>
        </View>
        {/* 底部行：用户信息 + 状态 */}
        <View style={styles.bottomRow}>
          <View style={styles.uploaderInfo}>
            {/* render avatar image when available, fallback to initial */}
            {receipt.uploader && (receipt.uploader as any).avatar ? (
              <Image source={{ uri: (receipt.uploader as any).avatar }} style={styles.avatarImage} />
            ) : (
              <View style={styles.avatarContainer}>
                <Text style={styles.avatarText}>
                  {((receipt.uploader && receipt.uploader.name) || '?').charAt(0).toUpperCase()}
                </Text>
              </View>
            )}
            <View style={styles.uploaderDetails}>
              <Text style={styles.uploaderName}>{receipt.uploader?.name || 'Unknown'}</Text>
              <Text style={styles.uploadDate}>{formatDate(receipt.uploadDate)}</Text>
            </View>
          </View>
          <View style={styles.statusSection}>
            <Icon name="description" size={16} color={MODULE_COLORS.travel} />
            <View style={[styles.statusBadge, { backgroundColor: statusStyle.backgroundColor }]}>
              <Text style={[styles.statusText, { color: statusStyle.color }]}>
                {statusStyle.text}
              </Text>
            </View>
          </View>
        </View>

        {/* Actions for approvals (display when pending and allowed) */}
        {receipt.status === 'pending' && (
          <View style={styles.approveActionsRow}>
            {typeof (onApprove) === 'function' && (
              <TouchableOpacity style={styles.approveButton} onPress={() => onApprove && onApprove(receipt.id)}>
                <Text style={styles.approveButtonText}>✓ Approve</Text>
              </TouchableOpacity>
            )}
            {typeof (onRequestReject) === 'function' && (
              <TouchableOpacity style={styles.rejectButton} onPress={() => onRequestReject && onRequestReject(receipt.id)}>
                <Text style={styles.rejectButtonText}>✕ Reject</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  mainCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    ...Platform.select({
      web: {
        boxShadow: '0px 2px 8px rgba(0,0,0,0.05)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 8,
        elevation: 2,
      },
    }),
    borderWidth: 1,
    borderColor: '#f1f5f9',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#111827',
  },
  actionButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 24,
    minWidth: 80,
    justifyContent: 'center',
  },
  pdfButton: {
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  uploadButton: {
    backgroundColor: MODULE_COLORS.travel,
    borderWidth: 0,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  pdfButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#666',
    marginLeft: 6,
  },
  uploadButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#fff',
    marginLeft: 6,
  },
  receiptCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: 12,
    padding: 12,
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  leftInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  categoryIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#fff5f5',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    flex: 1,
  },
  amountSection: {
    alignItems: 'flex-end',
  },
  amount: {
    fontSize: 18,
    fontWeight: '700',
    color: MODULE_COLORS.travel,
    marginBottom: 4,
  },
  localAmount: {
    fontSize: 14,
    color: '#6b7280',
  },
  exchangeRate: {
    fontSize: 12,
    color: '#9ca3af',
    marginTop: 2,
  },
  bottomRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  uploaderInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  avatarContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#e3f2fd',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  avatarImage: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginRight: 8,
  },
  avatarText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1976d2',
  },
  uploaderDetails: {
    flex: 1,
  },
  uploaderName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
    marginBottom: 2,
  },
  uploadDate: {
    fontSize: 12,
    color: '#6b7280',
  },
  statusSection: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  approveActionsRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 12,
    gap: 12,
  },
  approveButton: {
    backgroundColor: '#10b981',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  approveButtonText: {
    color: '#fff',
    fontWeight: '600',
  },
  rejectButton: {
    backgroundColor: '#ef4444',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  rejectButtonText: {
    color: '#fff',
    fontWeight: '600',
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginLeft: 8,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
  },
});

export default ReceiptCard;